<template>
	<!-- Login page -->
	<div class="auth-fluid" :class="appEnv">
		<div class="auth-fluid-form-box">
			<div class="align-items-center d-flex h-100">
				<div class="card-body">
					<div class="auth-brand text-center text-lg-left">
						<a href="/">
							<span>
								<img
									src="@/assets/images/logo.svg"
									alt="Lalamove logo"
									height="36"
								/>
							</span>
						</a>
					</div>

					<!-- Email form -->
					<template v-if="!confirmed">
						<h4 class="mt-0">{{ $t('Login.title') }}</h4>
						<p class="text-muted mb-4">
							{{ $t('PORTAL.Login.text_email') }}
						</p>

						<b-alert
							v-model="hasApiError"
							variant="danger"
							dismissible
						>
							<div>{{ apiError && apiError.message }}</div>
							<div
								v-if="apiError && apiError.requestId"
								class="font-10 mt-1"
							>
								Request ID: {{ apiError.requestId }}
							</div>
						</b-alert>

						<region-selector />
						<form
							class="needs-validation"
							novalidate
							@submit.prevent="submit"
						>
							<div class="form-group mb-3">
								<label for="emailaddress">
									{{ $t('PORTAL.Login.label_email') }}
								</label>
								<input
									id="emailaddress"
									v-model="email"
									class="form-control"
									type="email"
									required
									:placeholder="
										$t('PORTAL.Login.placeholder_email')
									"
									:class="`input-primary ${
										error ? 'is-invalid' : ''
									}`"
									:disabled="loading"
									@input="handleInput"
								/>
								<div class="invalid-feedback">
									<span>{{ $t(error) }}</span>
								</div>
							</div>
							<div class="form-group mb-0 text-center">
								<button
									class="btn btn-primary btn-block"
									type="submit"
									:disabled="loading"
								>
									<span
										v-if="loading"
										class="spinner-border spinner-border-sm mr-1"
										role="status"
										aria-hidden="true"
									/>
									<i v-else class="mdi mdi-email" />
									{{
										loading
											? $t('PORTAL.Login.button_loading')
											: $t('Common.button_submit')
									}}
								</button>
							</div>
						</form>
					</template>
					<!-- Email sent confirmation -->
					<template v-else>
						<div class="text-center w-75 m-auto">
							<img
								src="@/assets/images/mail_sent_llm.svg"
								alt="mail sent image"
								height="64"
							/>
							<h4 class="mt-4">
								{{ $t('PORTAL.Login.heading_confirm') }}
							</h4>
							<p class="text-muted mb-4">
								{{ $t('PORTAL.Login.text_confirm', { email }) }}
							</p>
						</div>

						<button
							class="btn btn-primary btn-block"
							type="button"
							@click="reset"
						>
							<i class="mdi mdi-reply" />
							{{ $t('PORTAL.Login.button_back_to_login') }}
						</button>
					</template>
					<footer class="footer footer-alt">
						<!-- For testing i18n: -->
						<b-dropdown dropup variant="link" size="lg" no-caret>
							<template #button-content>
								<i
									v-b-popover.hover.right="
										$t(
											'PORTAL.Home.popover_change_language'
										)
									"
									class="mdi mdi-translate noti-icon"
								/>
							</template>
							<b-dropdown-item
								v-for="locale in config.locales"
								:key="locale.id"
								href="#"
								:active="config.locale === locale.id"
								@click="changeLanguage(locale.id)"
							>
								{{ locale.translations[0].value }} ({{
									locale.translations[0].locale.split('-')[1]
								}})
							</b-dropdown-item>
						</b-dropdown>
						<p v-if="appEnv !== 'prd'" class="text-muted">
							{{ appEnv.toUpperCase() }}
						</p>
						<p class="text-muted">
							{{ new Date().getFullYear() }} © Lalamove+
						</p>
					</footer>
				</div>
			</div>
		</div>

		<div class="auth-fluid-right text-center" />
	</div>
</template>

<script>
import { authMethods, configMethods } from '@/store/helpers'
import RegionSelector from '@/components/RegionSelector.vue'
import Vue from 'vue'

const initState = () => ({
	email: '', // The email input.
	error: '', // Inline error (e.g. invalid email format).
	loading: false,
	hasApiError: false,
	apiError: null,
	confirmed: false // Email submitted successfully.
})

const { VUE_APP_ENV } = process.env

export default {
	page: {
		title: `${
			VUE_APP_ENV !== 'prd' ? VUE_APP_ENV.toUpperCase() : ''
		} Login`,
		meta: [
			{ name: 'description', content: `Log in to Lalamove+ Web Portal` }
		]
	},
	components: { RegionSelector },
	data() {
		return {
			...initState(),
			config: this.$store ? this.$store.state.config : {} || {},
			appEnv: VUE_APP_ENV
		}
	},
	async created() {
		await this.fetchLocales()
		await this.fetchRegions()
	},
	methods: {
		...authMethods,
		...configMethods,
		// On input changed, clear inline error.
		handleInput() {
			this.error = ''
		},
		async submit() {
			if (Vue.$cookies.get('auth_token')) {
				this.$router.go()
			} else {
				// Validate email format.
				const regexp = new RegExp(
					/^[\w-+]+(\.[\w-+]+)*@[\w-]+(\.[\w-]+)+$/i
				)
				if (!regexp.test(this.email)) {
					this.error = 'PORTAL.Login.error_invalid_email'
					return
				}
				this.loading = true
				this.hasApiError = false
				this.apiError = null
				// Submits email.
				try {
					await this.requestLoginLink({ email: this.email })
					this.confirmed = true
				} catch (error) {
					if (error.isApiError) {
						this.apiError = error
						this.hasApiError = true
					} else {
						this.error = error.message
					}
				}
				this.loading = false
			}
		},
		reset() {
			Object.assign(this.$data, initState())
		},
		changeLanguage(newLocale) {
			this.changeLocale({ newLocale })
		}
	}
}
</script>

<style scoped>
.auth-fluid.ldev,
.auth-fluid.stg {
	background: url('~~~@/assets/images/bg-auth-llm-french.jpg') center;
	background-size: cover;
}
</style>
